'use client';
import React, { useState } from 'react';

const BASE = 'https://cullertons-b4f8396.public.builtwithrocket.new';

export default function ValuationSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, sourceUrl: typeof window !== 'undefined' ? window.location.href : '' }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error || 'Something went wrong. Please try again.');
      }

      setSubmitted(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="valuation" className="py-24 md:py-32 bg-forest relative overflow-hidden">
      {/* Background orbs */}
      <div className="absolute inset-0 pointer-events-none opacity-5">
        <div
          className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-sage"
          style={{ transform: 'translate(30%, -30%)' }} />

        <div
          className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-sage-light"
          style={{ transform: 'translate(-20%, 30%)' }} />

      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-24 items-start">
          {/* Left: Contact info */}
          <div className="reveal">
            <span className="text-[11px] uppercase tracking-[0.2em] text-white font-semibold block mb-4">A Property Valuation</span>
            <h2
              className="font-serif text-cream leading-[0.92] tracking-tight mb-6"
              style={{ fontSize: 'clamp(2.4rem, 5vw, 4.5rem)' }}>

              <span className="italic text-gold">by Stephen</span>
            </h2>
            <p className="text-cream/60 max-w-sm leading-relaxed text-base font-light mb-6">For a discreet and confidential consultation, Stephen would be delighted to meet with you at a time of your convenience.

            </p>
            <p className="text-cream/60 max-w-sm leading-relaxed text-base font-light mb-10">
              Alternatively, we warmly invite you to visit our office on 74 St Stephen Street, Stockbridge, Edinburgh, EH3 5AQ.
            </p>
            <div className="space-y-5">
              <a href="tel:+441312255007" className="flex items-center gap-4 group">
                <div className="w-10 h-10 rounded-xl bg-sage/15 flex items-center justify-center flex-shrink-0 group-hover:bg-sage/30 transition-colors">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8A9E7B" strokeWidth="2">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2.18h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.16 6.16l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                  </svg>
                </div>
                <span className="text-cream/65 text-sm group-hover:text-cream transition-colors">+44 131 225 5007</span>
              </a>
              <a href="mailto:info@cullertons.co.uk" className="flex items-center gap-4 group">
                <div className="w-10 h-10 rounded-xl bg-sage/15 flex items-center justify-center flex-shrink-0 group-hover:bg-sage/30 transition-colors">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8A9E7B" strokeWidth="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                </div>
                <span className="text-cream/65 text-sm group-hover:text-cream transition-colors">info@cullertons.co.uk</span>
              </a>
              <a href="#" className="flex items-center gap-4 group">
                <div className="w-10 h-10 rounded-xl bg-sage/15 flex items-center justify-center flex-shrink-0 group-hover:bg-sage/30 transition-colors">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8A9E7B" strokeWidth="2">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                </div>
                <span className="text-cream/65 text-sm group-hover:text-cream transition-colors">74 St Stephen Street, Edinburgh EH3 5AQ</span>
              </a>
            </div>
          </div>

          {/* Right: Form */}
          <div className="reveal delay-200">
            <form
              onSubmit={handleSubmit}
              className="rounded-3xl bg-cream/5 border border-cream/15 p-8 md:p-10 space-y-7">

              <h3 className="font-serif text-cream text-2xl mb-1">Book a consultation</h3>
              <p className="text-[#CFB786] text-xs uppercase tracking-widest">No obligation · Just expert advice</p>

              {submitted ?
              <div className="py-10 flex flex-col items-center text-center gap-6">
                  {/* Success icon */}
                  <div className="w-16 h-16 rounded-full bg-sage/20 border border-sage/40 flex items-center justify-center flex-shrink-0">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#8A9E7B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>

                  {/* Heading */}
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-sage font-semibold mb-3">Enquiry Received</p>
                    <h3 className="font-serif text-cream text-2xl leading-snug mb-3">
                      Thank you,<br /><em className="text-gold">{formData.name.split(' ')[0] || 'there'}</em>
                    </h3>
                    <p className="text-cream/55 text-sm leading-relaxed max-w-xs mx-auto">
                      Your consultation request has been sent to the Cullertons New Town team. Stephen will be in touch with you shortly.
                    </p>
                  </div>

                  {/* Divider */}
                  <div className="w-12 h-px bg-cream/15" />

                  {/* Contact nudge */}
                  <p className="text-cream/35 text-[11px] leading-relaxed">
                    In the meantime, you can reach us at{' '}
                    <a href="tel:+441312255007" className="text-sage hover:text-cream transition-colors">+44 131 225 5007</a>
                  </p>
                </div> :

              <>
                  <div>
                    <label className="block text-[11px] uppercase tracking-[0.18em] text-cream/50 mb-2 font-medium">Your Name</label>
                    <input
                    type="text"
                    placeholder="e.g. James Robertson"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full bg-transparent border-b border-cream/20 py-3 text-cream placeholder-cream/25 focus:border-sage focus:outline-none transition-colors text-sm" />

                  </div>
                  <div>
                    <label className="block text-[11px] uppercase tracking-[0.18em] text-cream/50 mb-2 font-medium">Email Address</label>
                    <input
                    type="email"
                    placeholder="your@email.com"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full bg-transparent border-b border-cream/20 py-3 text-cream placeholder-cream/25 focus:border-sage focus:outline-none transition-colors text-sm" />

                  </div>
                  <div>
                    <label className="block text-[11px] uppercase tracking-[0.18em] text-cream/50 mb-2 font-medium">Phone Number</label>
                    <input
                    type="tel"
                    placeholder="+44 7700 000000"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full bg-transparent border-b border-cream/20 py-3 text-cream placeholder-cream/25 focus:border-sage focus:outline-none transition-colors text-sm" />

                  </div>
                  <div>
                    <label className="block text-[11px] uppercase tracking-[0.18em] text-cream/50 mb-2 font-medium">Property Address</label>
                    <input
                    type="text"
                    placeholder="e.g. 12 Craigcrook Road, EH4"
                    required
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="w-full bg-transparent border-b border-cream/20 py-3 text-cream placeholder-cream/25 focus:border-sage focus:outline-none transition-colors text-sm" />

                  </div>
                  <div>
                    <label className="block text-[11px] uppercase tracking-[0.18em] text-cream/50 mb-2 font-medium">Anything else you&apos;d like us to know?</label>
                    <textarea
                    rows={2}
                    placeholder="e.g. timeframe, property type, any questions..."
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="w-full bg-transparent border-b border-cream/20 py-3 text-cream placeholder-cream/25 focus:border-sage focus:outline-none transition-colors text-sm resize-none" />

                  </div>

                  {error && (
                    <p className="text-[13px] text-red-400 text-center">{error}</p>
                  )}

                  <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-between bg-cream text-forest text-xs uppercase tracking-[0.2em] font-semibold px-7 py-5 rounded-full hover:bg-sage-light transition-colors group disabled:opacity-60 disabled:cursor-not-allowed">

                    <span>{loading ? 'Sending…' : 'Request a consultation'}</span>
                    {!loading && (
                      <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.2"
                      className="group-hover:translate-x-1 transition-transform">

                        <path d="M5 12h14M12 5l7 7-7 7" />
                      </svg>
                    )}
                  </button>
                  <p className="text-center text-[11px] text-cream/30">Your details are kept confidential and never shared.</p>
                </>
              }
            </form>
          </div>
        </div>
      </div>
    </section>);

}